import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-shim";
import { useEffect, useRef, useState } from "react";
import { getMyTrip, updateTripDates } from "@/lib/trip.functions";
import { getUploadRules, saveUploadRules } from "@/lib/media.functions";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import type { DateRange } from "react-day-picker";
import { Wifi, Zap, Hand, BatteryCharging, AlertTriangle, Plug, Upload as UploadIcon } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  isNative,
  getConnectionType,
  getBatteryInfo,
  type ConnectionType,
  type BatteryInfo,
} from "@/lib/native";
import {
  useLocalUploadRules,
  setLocalUploadRules,
  mirrorServerUploadRules,
} from "@/lib/offline/upload-rules-local";
import { useCachedTrip } from "@/lib/offline/trip-cache";
import {
  RoavaPage,
  RoavaCard,
  SelectableCard,
  SettingRow,
  StatusChip,
  PrimaryButton,
} from "@/components/roava";
import { SyncOutboxCard } from "@/components/SyncOutboxCard";


export const Route = createFileRoute("/_authenticated/upload-mode")({
  validateSearch: (s: Record<string, unknown>): { focus?: "auto" | "rules" } => ({
    focus: s.focus === "auto" || s.focus === "rules" ? (s.focus as "auto" | "rules") : undefined,
  }),
  head: () =>
    pageMeta({
      title: "Upload & sync settings — Roava by WGP Apps",
      description: "Choose how Roava uploads trip photos and videos.",
      path: "/upload-mode",
    }),
  component: UploadModePage,
});

type Mode = "auto" | "manual";

function formatTripDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}


function UploadModePage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const search = Route.useSearch();
  const tripFn = useServerFn(getMyTrip);
  const getFn = useServerFn(getUploadRules);
  const saveFn = useServerFn(saveUploadRules);
  const updateDatesFn = useServerFn(updateTripDates);

  const cachedTrip = useCachedTrip();
  const { data: tripData } = useQuery({
    queryKey: ["my-trip"],
    queryFn: () => tripFn({}),
    retry: 1,
  });
  // Local-first trip id: works offline as long as the user has launched once online.
  const tripId = tripData?.trip?.id ?? cachedTrip?.id ?? null;
  const networkOnline = typeof navigator === "undefined" || navigator.onLine !== false;

  const { data: rules } = useQuery({
    queryKey: ["upload-rules", tripId],
    queryFn: () => getFn({ data: { tripId: tripId! } }),
    enabled: !!tripId && networkOnline,
  });

  // Mirror server rules into the local cache so the native upload gate and
  // the /offline-control screen see the same source of truth.
  useEffect(() => {
    if (rules && tripId) void mirrorServerUploadRules(rules, tripId);
  }, [rules, tripId]);

  // Hydrate the form from local rules immediately so the screen never shows
  // stale defaults when opened offline.
  const localRules = useLocalUploadRules(tripId);

  const [mode, setMode] = useState<Mode>(localRules.uploadMode);
  const [minBattery, setMinBattery] = useState(localRules.minBatteryPct);
  const [requireCharging, setRequireCharging] = useState(localRules.requireChargingUnderMin);
  const [wifiOnly, setWifiOnly] = useState(localRules.wifiOnly);
  const [cellularConfirmOpen, setCellularConfirmOpen] = useState(false);
  const [tripDatesOpen, setTripDatesOpen] = useState(false);
  const [tripDateRange, setTripDateRange] = useState<DateRange | undefined>();
  const hydrated = useRef(false);
  const rulesRef = useRef<HTMLDivElement | null>(null);
  const [highlightRules, setHighlightRules] = useState(false);


  useEffect(() => {
    // Prefer server rules once they arrive. Until then, the local cache values
    // already populated state in useState initializers above.
    if (rules && !hydrated.current) {
      setMode(search.focus === "auto" ? "auto" : rules.uploadMode);
      setMinBattery(rules.minBatteryPct);
      setRequireCharging(rules.requireChargingUnderMin);
      setWifiOnly(rules.wifiOnly);
      hydrated.current = true;
    }
  }, [rules, search.focus]);

  // When opened via "Edit rules", scroll the Upload rules card into view and
  // briefly ring it so the user can see where they landed.
  useEffect(() => {
    if (search.focus !== "rules" || !hydrated.current) return;
    const el = rulesRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setHighlightRules(true);
    const t = window.setTimeout(() => setHighlightRules(false), 1800);
    return () => window.clearTimeout(t);
  }, [search.focus, hydrated.current]);

  // Live status pills (Wi-Fi + battery) — native only. On web both APIs are
  // unreliable (mobile Safari doesn't implement getBattery; navigator.connection
  // varies), so we hide the whole "Right now" block off-native rather than show
  // fake numbers.
  const [conn, setConn] = useState<ConnectionType>("unknown");
  const [battery, setBattery] = useState<BatteryInfo>({ level: null, isCharging: null });
  useEffect(() => {
    if (!isNative()) return;
    let alive = true;
    async function poll() {
      try {
        const [c, b] = await Promise.all([getConnectionType(), getBatteryInfo()]);
        if (!alive) return;
        setConn(c);
        setBattery(b);
      } catch {
        /* ignore */
      }
    }
    poll();
    const t = window.setInterval(poll, 10_000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, []);

  // Silent auto-save just for the Wi-Fi only toggle, so travelers don't have
  // to find the Save button at the bottom of the screen (and so the change
  // doesn't get lost if they back out). Mirrors the pattern in
  // /offline-control's patchRules({ wifiOnly }, { silent: true }).
  async function persistWifiOnly(next: boolean) {
    if (!tripId) return;
    const payload = {
      wifiOnly: next,
      uploadOriginals: rules?.uploadOriginals ?? localRules.uploadOriginals,
      compressedPreviewsFirst: rules?.compressedPreviewsFirst ?? localRules.compressedPreviewsFirst,
      uploadMode: mode,
      minBatteryPct: minBattery,
      requireChargingUnderMin: requireCharging,
    };
    try {
      await setLocalUploadRules(payload, tripId);
      if (typeof navigator === "undefined" || navigator.onLine !== false) {
        try {
          await saveFn({ data: { tripId, ...payload } });
          await mirrorServerUploadRules(payload, tripId);
        } catch (e) {
          console.warn("[upload-mode] wifiOnly server push failed, kept local", e);
        }
      }
      qc.invalidateQueries({ queryKey: ["upload-rules", tripId] });
      toast.success(
        next
          ? "Wi-Fi only turned on."
          : "Wi-Fi only turned off — uploads can use cellular.",
      );
    } catch (e) {
      console.warn("[upload-mode] wifiOnly local save failed", e);
      toast.error("Couldn't save that change. Try again.");
    }
  }

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        wifiOnly,
        uploadOriginals: rules?.uploadOriginals ?? localRules.uploadOriginals,
        compressedPreviewsFirst: rules?.compressedPreviewsFirst ?? localRules.compressedPreviewsFirst,
        uploadMode: mode,
        minBatteryPct: minBattery,
        requireChargingUnderMin: requireCharging,
      };
      // Local-first: persist immediately so the native upload gate and the
      // offline-control screen see the new values right away. The dirty flag
      // tells settings-sync to push on next reconnect.
      await setLocalUploadRules(payload, tripId!);
      // If online, push to the server now so other devices catch up.
      if (typeof navigator === "undefined" || navigator.onLine !== false) {
        try {
          await saveFn({ data: { tripId: tripId!, ...payload } });
          // Mirror back as clean so the dirty flag clears.
          await mirrorServerUploadRules(payload, tripId!);
        } catch (e) {
          // Push failed even though we appeared online — keep dirty flag,
          // settings-sync will retry on next connectivity event.
          console.warn("[upload-mode] server push failed, will retry", e);
          throw e;
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["upload-rules", tripId] });
      toast.success("Upload mode saved");
      nav({ to: "/permissions" });
    },
    onError: (_e: any) => {
      // Local save already happened in mutationFn, so this is server-only failure.
      toast.success("Saved on this device — will sync when online");
      nav({ to: "/permissions" });
    },
  });

  const saveTripDates = useMutation({
    mutationFn: () => {
      const from = tripDateRange?.from;
      const to = tripDateRange?.to;
      if (!from || !to) throw new Error("Pick a start and end date");
      const fmt = (d: Date) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      return updateDatesFn({
        data: { tripId: tripId!, startDate: fmt(from), endDate: fmt(to) },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-trip"] });
      toast.success("Trip dates saved");
      setTripDatesOpen(false);
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not save trip dates"),
  });

  const batteryUnknown = battery.level === null;
  const batteryPct = battery.level !== null ? Math.round(battery.level * 100) : null;

  if (!tripId) {
    return (
      <RoavaPage title="Join a trip first" subtitle="Enter your trip invite code to continue.">
        <PrimaryButton onClick={() => nav({ to: "/join", search: {} })} className="h-14 rounded-2xl w-full">
          Enter invite code
        </PrimaryButton>
      </RoavaPage>
    );
  }

  const tripRangeLabel =
    tripData?.trip?.startDate && tripData?.trip?.endDate
      ? `${formatTripDate(tripData.trip.startDate)} – ${formatTripDate(tripData.trip.endDate)}`
      : "Not set";

  const networkLabel =
    conn === "wifi" ? "Wi-Fi" : conn === "cellular" ? "Cellular" : conn === "none" ? "Offline" : "Unknown";
  const networkOk = conn === "wifi" || (conn === "cellular" && !wifiOnly);
  const batteryMeetsMin =
    batteryUnknown
      ? true
      : batteryPct! >= minBattery || (requireCharging && battery.isCharging === true);
  const uploadStatusOk = networkOk && batteryMeetsMin;

  // Traveler-friendly explanation of why auto-uploads are/aren't running right now.
  let uploadGateLabel: string;
  let uploadGateHint: string;
  if (uploadStatusOk) {
    uploadGateLabel = "Ready to upload";
    uploadGateHint = "Auto-uploads will run whenever there's new trip media to send.";
  } else if (conn === "none") {
    uploadGateLabel = "Paused — no connection";
    uploadGateHint = "We'll resume automatically as soon as you're back online.";
  } else if (conn === "cellular" && wifiOnly) {
    uploadGateLabel = "Paused — needs Wi-Fi";
    uploadGateHint = "Connect to Wi-Fi, or turn off \u201CWi-Fi only\u201D above to allow cellular uploads.";
  } else if (!batteryMeetsMin) {
    uploadGateLabel = "Paused — battery too low";
    uploadGateHint = batteryUnknown
      ? "Waiting for a battery reading from your phone."
      : `Battery is ${batteryPct}%. Minimum is ${minBattery}%. Plug in${requireCharging ? "" : " or lower the minimum"} to resume.`;
  } else {
    uploadGateLabel = "Paused";
    uploadGateHint = "Conditions don't match your upload rules yet.";
  }

  return (
    <RoavaPage
      title="Upload & sync settings"
      subtitle="Choose how Roava uploads trip photos and videos."
      action={
        <Button asChild variant="ghost" size="sm" className="h-8 px-3 text-xs">
          <Link to="/profile">My profile</Link>
        </Button>
      }
    >
      <div className="grid gap-4">
        {/* Upload mode */}
        <RoavaCard title="Upload mode" flush className="p-6">
          <div className="grid gap-3">
            <SelectableCard
              selected={mode === "auto"}
              onSelect={() => setMode("auto")}
              icon={<Zap className="size-5" />}
              title="Automatic uploads"
              badge={
                <span className="text-[10px] font-semibold uppercase tracking-wide bg-[var(--roava-green)]/15 text-[var(--roava-green)] px-1.5 py-0.5 rounded">
                  Recommended
                </span>
              }
              description="Roava finds photos and videos from your trip date range and uploads them when your upload rules allow."
            >
              {mode === "auto" && (
                <div className="border-t border-[var(--roava-border)] pt-3" onClick={(e) => e.stopPropagation()}>
                  <p className="text-xs font-semibold text-[var(--roava-text-muted)] uppercase tracking-wide mb-1">
                    Trip media date range
                  </p>
                  <p className="text-sm font-medium">{tripRangeLabel}</p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      const s = tripData?.trip?.startDate;
                      const en = tripData?.trip?.endDate;
                      setTripDateRange(
                        s && en ? { from: new Date(s), to: new Date(en) } : undefined,
                      );
                      setTripDatesOpen(true);
                    }}
                    className="text-sm font-medium text-[var(--roava-blue)] mt-2 underline-offset-2 hover:underline"
                  >
                    {tripData?.trip?.startDate && tripData?.trip?.endDate ? "Edit trip dates →" : "Set trip dates →"}
                  </button>
                  <p className="text-xs text-[var(--roava-text-muted)] mt-2">
                    Roava uses this range for Review trip media and automatic trip media matching. Manual uploads do not use this range.
                  </p>
                </div>
              )}
            </SelectableCard>

            <SelectableCard
              selected={mode === "manual"}
              onSelect={() => setMode("manual")}
              icon={<Hand className="size-5" />}
              title="Manual uploads"
              description="Nothing uploads automatically. You choose photos and videos yourself."
            >
              {mode === "manual" && (
                <div className="border-t border-[var(--roava-border)] pt-3" onClick={(e) => e.stopPropagation()}>
                  <p className="text-xs text-[var(--roava-text-muted)]">
                    Manual uploads do not use the trip date range. Photos keep their original GPS and camera metadata.
                  </p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      nav({ to: "/upload" });
                    }}
                    className="text-sm font-medium text-[var(--roava-blue)] mt-2 underline-offset-2 hover:underline"
                  >
                    Go to Uploads →
                  </button>
                </div>
              )}
            </SelectableCard>
          </div>
        </RoavaCard>

        {/* Upload rules */}
        <div
          ref={rulesRef}
          className={cn(
            "rounded-[24px] transition",
            highlightRules && "ring-2 ring-[var(--roava-blue)]/40",
          )}
        >
          <RoavaCard
            title="Upload rules"
            description="These rules control upload & sync only. Tracking battery protection is handled separately by Tracking battery guard."
          >
            <div className="grid">
              <SettingRow
                icon={<Wifi className="size-4" />}
                label="Upload on Wi-Fi only"
                description="Avoid expensive international data charges."
                control={
                  <Switch
                    checked={wifiOnly}
                    onCheckedChange={(v) => {
                      if (v) {
                        setWifiOnly(true);
                        void persistWifiOnly(true);
                      } else {
                        setCellularConfirmOpen(true);
                      }
                    }}
                  />
                }
              />

              <SettingRow
                icon={<BatteryCharging className="size-4" />}
                label={
                  <div className="flex items-center justify-between gap-3">
                    <span>Minimum battery to upload</span>
                    <span className="text-sm font-mono tabular-nums text-[var(--roava-text-muted)]">
                      {minBattery === 0 ? "off" : `${minBattery}%`}
                    </span>
                  </div>
                }
                description="Set to 0 to ignore battery for uploads."
              />
              <div className="pb-3 -mt-1">
                <Slider
                  value={[minBattery]}
                  min={0}
                  max={100}
                  step={5}
                  onValueChange={(v) => setMinBattery(v[0] ?? 0)}
                />
              </div>

              <SettingRow
                icon={<Plug className="size-4" />}
                label="Allow uploads while charging"
                description="Allow uploads under the minimum while the device is charging."
                control={<Switch checked={requireCharging} onCheckedChange={setRequireCharging} />}
              />
            </div>
          </RoavaCard>
        </div>

        {/* Pending GPS pings — Sync now button (auto-hides when empty) */}
        <SyncOutboxCard />

        {/* Current device status — native only */}
        {isNative() && (
          <RoavaCard title="Current device status">
            <div className="grid">
              <SettingRow
                icon={<BatteryCharging className="size-4" />}
                label="Battery"
                control={
                  <StatusChip variant={batteryUnknown ? "neutral" : batteryMeetsMin ? "success" : "warning"}>
                    {batteryUnknown ? "Unknown" : `${batteryPct}%`}
                  </StatusChip>
                }
              />
              <SettingRow
                icon={<Plug className="size-4" />}
                label="Charging"
                control={
                  <StatusChip variant={battery.isCharging ? "success" : "neutral"}>
                    {battery.isCharging === null ? "Unknown" : battery.isCharging ? "Plugged in" : "On battery"}
                  </StatusChip>
                }
              />
              <SettingRow
                icon={<Wifi className="size-4" />}
                label="Network"
                control={
                  <StatusChip variant={networkOk ? "success" : conn === "none" ? "warning" : "neutral"}>
                    {networkLabel}
                  </StatusChip>
                }
              />
              <SettingRow
                icon={<UploadIcon className="size-4" />}
                label="Auto-uploads"
                description={uploadGateHint}
                control={
                  <StatusChip variant={uploadStatusOk ? "success" : "warning"}>
                    {uploadGateLabel}
                  </StatusChip>
                }
              />
            </div>
            {batteryUnknown && (
              <p className="text-xs text-[var(--roava-orange)] mt-3 flex items-start gap-1.5">
                <AlertTriangle className="size-3.5 shrink-0 mt-0.5" />
                Battery level not available yet. Wi-Fi check still works.
              </p>
            )}
          </RoavaCard>
        )}

        <PrimaryButton
          onClick={() => save.mutate()}
          disabled={save.isPending || !hydrated.current}
          className="mt-2 h-14 rounded-2xl w-full text-base font-semibold"
        >
          {save.isPending ? "Saving…" : "Save"}
        </PrimaryButton>
      </div>

      <AlertDialog open={cellularConfirmOpen} onOpenChange={setCellularConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Use cellular data for uploads?</AlertDialogTitle>
            <AlertDialogDescription>
              Photos and videos are large. On cellular — especially roaming or
              an international SIM — uploads can use a lot of data and may cost
              money. You can change this anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2">
            <AlertDialogAction
              onClick={() => {
                setWifiOnly(false);
                void persistWifiOnly(false);
              }}
              className="bg-[var(--roava-blue)] text-white hover:bg-[var(--roava-blue)]/90 font-bold h-12 w-full text-base"
            >
              Use cellular data
            </AlertDialogAction>
            <AlertDialogCancel
              className="bg-transparent border border-[var(--roava-border)] text-[var(--roava-text)] hover:bg-[var(--roava-surface-muted)] font-medium h-12 w-full text-base mt-0"
            >
              Keep Wi-Fi only
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      <Dialog open={tripDatesOpen} onOpenChange={setTripDatesOpen}>
        <DialogContent className="max-w-[22rem]">
          <DialogHeader>
            <DialogTitle>Trip date range</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">Start date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-11 justify-start font-normal">
                    {tripDateRange?.from ? formatTripDate(tripDateRange.from.toISOString()) : "Pick a start date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 pointer-events-auto" align="start">
                  <Calendar
                    mode="single"
                    selected={tripDateRange?.from}
                    onSelect={(d) =>
                      setTripDateRange((prev) => ({
                        from: d ?? undefined,
                        to: prev?.to && d && prev.to < d ? undefined : prev?.to,
                      }))
                    }
                    disabled={tripDateRange?.to ? { after: tripDateRange.to } : undefined}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">End date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-11 justify-start font-normal">
                    {tripDateRange?.to ? formatTripDate(tripDateRange.to.toISOString()) : "Pick an end date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 pointer-events-auto" align="start">
                  <Calendar
                    mode="single"
                    selected={tripDateRange?.to}
                    onSelect={(d) =>
                      setTripDateRange((prev) => ({
                        from: prev?.from,
                        to: d ?? undefined,
                      }))
                    }
                    disabled={tripDateRange?.from ? { before: tripDateRange.from } : undefined}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              onClick={() => saveTripDates.mutate()}
              disabled={saveTripDates.isPending || !tripDateRange?.from || !tripDateRange?.to}
              className="h-12 w-full"
            >
              {saveTripDates.isPending ? "Saving…" : "Save dates"}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setTripDatesOpen(false)}
              className="h-10 w-full"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </RoavaPage>
  );
}
